import { useState } from 'react';
import { X } from 'lucide-react';

/**
 * 5-field price entry modal. No CEX toggle — cash + voucher are both visible at once.
 * Validation queries (inline, non-blocking):
 *  - Cash > Voucher → "usually it's the other way round, swap?"
 *  - Cash == Voucher && value > £0.10 → "both identical — confirm?"
 *    (£0.01=£0.01 and £0.10=£0.10 match silently — common decline pairs.)
 */

const FIELDS = [
  { key: 'musicmagpie',  label: 'Music Magpie',    caps: false },
  { key: 'webuybooks',   label: 'We Buy Books',    caps: false },
  { key: 'worldofbooks', label: 'World of Books',  caps: false },
  { key: 'cex_cash',     label: 'CEX CASH',        caps: true  },
  { key: 'cex_voucher',  label: 'CEX VOUCHER',     caps: true  },
];

const SILENT_MATCH_VALUES = new Set(['0.01', '0.10']);

export default function PriceEntryModal({ barcode, onClose, onSubmit, onSkip }) {
  const [prices, setPrices] = useState({
    musicmagpie: '', webuybooks: '', worldofbooks: '', cex_cash: '', cex_voucher: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [query, setQuery] = useState(null); // { type, message, accept: fn, fix: fn }

  const cexCash = parseFloat(prices.cex_cash);
  const cexVoucher = parseFloat(prices.cex_voucher);
  const bothCexFilled = !isNaN(cexCash) && !isNaN(cexVoucher);

  const swapCex = () => {
    setPrices((p) => ({ ...p, cex_cash: p.cex_voucher, cex_voucher: p.cex_cash }));
    setQuery(null);
  };

  const acceptAsIs = () => setQuery(null);

  const runValidationQueries = () => {
    if (!bothCexFilled) return null;
    if (cexCash > cexVoucher) {
      return {
        type: 'cash_gt_voucher',
        message: `CEX Cash (£${cexCash.toFixed(2)}) is HIGHER than Voucher (£${cexVoucher.toFixed(2)}). Vouchers normally pay more than cash — did you enter them the wrong way round?`,
        primary: { label: 'Yes, swap them', action: swapCex },
        secondary: { label: "No, that's correct — accept", action: acceptAsIs },
      };
    }
    if (cexCash === cexVoucher && cexCash > 0) {
      // silent-match short-circuit
      const asStr = cexCash.toFixed(2);
      if (SILENT_MATCH_VALUES.has(asStr)) return null;
      return {
        type: 'cex_match_over_10p',
        message: `CEX Cash and Voucher are both £${cexCash.toFixed(2)}. Identical prices over 10p are unusual — please confirm.`,
        primary: { label: "Yes, they're both that price", action: acceptAsIs },
        secondary: { label: 'Let me fix it', action: () => setQuery(null) },
      };
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const q = runValidationQueries();
    if (q) {
      setQuery(q);
      return;
    }

    setIsSubmitting(true);
    await onSubmit({
      barcode,
      musicmagpie:  parseFloat(prices.musicmagpie)  || 0,
      webuybooks:   parseFloat(prices.webuybooks)   || 0,
      worldofbooks: parseFloat(prices.worldofbooks) || 0,
      cex_cash:     parseFloat(prices.cex_cash)     || 0,
      cex_voucher:  parseFloat(prices.cex_voucher)  || 0,
    });
    setIsSubmitting(false);
  };

  const setAllToZero = () => setPrices({
    musicmagpie: '0', webuybooks: '0', worldofbooks: '0', cex_cash: '0', cex_voucher: '0',
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="price-entry-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Enter <span className="text-[#00D26A]">Prices</span></h2>
            <p className="text-sm text-zinc-400 mt-1 font-mono">Barcode: <span className="text-[#FFD700]">{barcode}</span></p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors" data-testid="price-entry-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="bg-[#00D26A]/10 border border-[#00D26A]/40 rounded-lg p-4 mb-6">
            <p className="text-sm text-[#00D26A]">
              💰 <span className="font-semibold">Earn 1p per price check!</span> Enter prices from each buyer's website or app.
            </p>
          </div>

          <div className="space-y-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label
                  htmlFor={`price-input-${f.key}`}
                  className={`block text-sm mb-2 ${f.caps ? 'font-bold tracking-wider text-[#FFD700]' : 'font-semibold text-zinc-300'}`}
                >
                  {f.label}
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">£</span>
                  <input
                    id={`price-input-${f.key}`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={prices[f.key]}
                    onChange={(e) => setPrices({ ...prices, [f.key]: e.target.value })}
                    className="flex-1 px-4 py-3 bg-black/40 border border-zinc-800 rounded-lg text-[#FFD700] font-mono focus:ring-2 focus:ring-[#00D26A] focus:border-transparent placeholder:text-zinc-600"
                    placeholder="0.00"
                    data-testid={`price-input-${f.key}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {query && (
            <div
              className="mt-4 bg-[#FFD700]/10 border border-[#FFD700]/40 rounded-lg p-4"
              data-testid="price-validation-query"
            >
              <p className="text-sm text-white mb-3">⚠️ {query.message}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={query.primary.action}
                  className="flex-1 px-4 py-2 bg-[#FFD700] text-black rounded-lg text-sm font-semibold hover:brightness-110"
                  data-testid="price-query-primary"
                >
                  {query.primary.label}
                </button>
                <button
                  type="button"
                  onClick={query.secondary.action}
                  className="flex-1 px-4 py-2 bg-zinc-800 text-zinc-200 rounded-lg text-sm font-medium hover:bg-zinc-700"
                  data-testid="price-query-secondary"
                >
                  {query.secondary.label}
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={setAllToZero}
            className="mt-4 text-sm text-zinc-500 hover:text-zinc-300 underline"
            data-testid="price-set-zero"
          >
            Set all to £0.00 (item not accepted anywhere)
          </button>

          <div className="flex gap-3 mt-8">
            <button
              type="button"
              onClick={onSkip}
              className="flex-1 px-6 py-3 bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-lg font-semibold hover:bg-zinc-700 transition-colors"
              disabled={isSubmitting}
              data-testid="price-entry-skip"
            >
              Skip (Check Later)
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-[#00D26A] text-black rounded-lg font-bold hover:brightness-110 transition-colors disabled:bg-zinc-700 disabled:text-zinc-400"
              disabled={isSubmitting}
              data-testid="price-entry-submit"
            >
              {isSubmitting ? 'Submitting…' : 'Submit Prices (5p total · 1p each)'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
